/**
 * rock_shape.ts — the PURE half of the procedural rocks (2026-09-25, visual-realism R3): the kinds, their
 * specs, the seeded generator (cube-sphere → superellipsoid → fracture planes → noise + strata → a flat
 * base at y = 0) as plain arrays, and each (kind, variant)'s real HEIGHT — what the scatter buries against.
 * No three.js: the flora scatter worker plants against these without loading a renderer module.
 */
export type RockKind = 'cliff' | 'outcrop' | 'boulder' | 'talus' | 'pebble';
export const ROCK_KINDS: readonly RockKind[] = ['cliff', 'outcrop', 'boulder', 'talus', 'pebble'];

interface KindSpec { n: number; size: [number, number, number]; power: number; planes: number; planeDepth: [number, number]; noise: number; freq: number; strata: number; variants: number }
/** Size = (width X, height Y, depth Z) in metres at scale 1. A cliff's FACE looks down +Z. */
export const ROCK_SPEC: Record<RockKind, KindSpec> = {
  cliff: { n: 16, size: [11, 9, 4.2], power: 4.2, planes: 7, planeDepth: [0.62, 0.9], noise: 0.05, freq: 2.2, strata: 0.07, variants: 8 },
  outcrop: { n: 12, size: [6.5, 3.6, 5.2], power: 2.8, planes: 9, planeDepth: [0.55, 0.85], noise: 0.07, freq: 2.0, strata: 0.04, variants: 6 },
  boulder: { n: 9, size: [2.3, 1.55, 1.95], power: 2.3, planes: 7, planeDepth: [0.6, 0.9], noise: 0.07, freq: 2.4, strata: 0, variants: 6 },
  talus: { n: 5, size: [0.75, 0.45, 0.6], power: 2.2, planes: 5, planeDepth: [0.55, 0.85], noise: 0.08, freq: 2.2, strata: 0, variants: 5 },
  pebble: { n: 3, size: [0.24, 0.12, 0.19], power: 2.0, planes: 3, planeDepth: [0.6, 0.9], noise: 0.08, freq: 2.0, strata: 0, variants: 4 },
};

/** Deterministic PRNG (mulberry32). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function hash3(x: number, y: number, z: number, s: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(z | 0, 2147483647) ^ Math.imul(s | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/** 3D value noise in [−1, 1] (trilinear, smoothstep fade). */
export function noise3(x: number, y: number, z: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const fx = x - xi, fy = y - yi, fz = z - zi;
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy), w = fz * fz * (3 - 2 * fz);
  const c = (dx: number, dy: number, dz: number): number => hash3(xi + dx, yi + dy, zi + dz, s);
  const x00 = c(0, 0, 0) + (c(1, 0, 0) - c(0, 0, 0)) * u, x10 = c(0, 1, 0) + (c(1, 1, 0) - c(0, 1, 0)) * u;
  const x01 = c(0, 0, 1) + (c(1, 0, 1) - c(0, 0, 1)) * u, x11 = c(0, 1, 1) + (c(1, 1, 1) - c(0, 1, 1)) * u;
  const y0 = x00 + (x10 - x00) * v, y1 = x01 + (x11 - x01) * v;
  return (y0 + (y1 - y0) * w) * 2 - 1;
}
function fbm(x: number, y: number, z: number, s: number): number { return noise3(x, y, z, s) * 0.6 + noise3(x * 2.1, y * 2.1, z * 2.1, s + 1) * 0.28 + noise3(x * 4.3, y * 4.3, z * 4.3, s + 2) * 0.12; }

/** A watertight cube-sphere: n × n quads per face, seam vertices shared. */
function cubeSphere(n: number): { pos: number[]; idx: number[] } {
  const pos: number[] = []; const idx: number[] = []; const map = new Map<string, number>();
  const vid = (x: number, y: number, z: number): number => {
    const k = `${Math.round(x * 1e5)},${Math.round(y * 1e5)},${Math.round(z * 1e5)}`;
    let i = map.get(k); if (i === undefined) { i = pos.length / 3; pos.push(x, y, z); map.set(k, i); } return i;
  };
  const faces: Array<[number[], number[], number[]]> = [
    [[1, 0, 0], [0, 0, -1], [0, 1, 0]], [[-1, 0, 0], [0, 0, 1], [0, 1, 0]], [[0, 1, 0], [1, 0, 0], [0, 0, -1]],
    [[0, -1, 0], [1, 0, 0], [0, 0, 1]], [[0, 0, 1], [1, 0, 0], [0, 1, 0]], [[0, 0, -1], [-1, 0, 0], [0, 1, 0]],
  ];
  for (const [N, U, V] of faces) {
    const g: number[][] = [];
    for (let j = 0; j <= n; j++) { const row: number[] = []; for (let i = 0; i <= n; i++) { const a = (i / n) * 2 - 1, b = (j / n) * 2 - 1; row.push(vid(N[0] + U[0] * a + V[0] * b, N[1] + U[1] * a + V[1] * b, N[2] + U[2] * a + V[2] * b)); } g.push(row); }
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) { const a = g[j][i], b = g[j][i + 1], c = g[j + 1][i + 1], d = g[j + 1][i]; idx.push(a, b, c, a, c, d); }
  }
  return { pos, idx };
}

export function rockShape(kind: RockKind, variant: number, n: number): { pos: Float32Array; idx: number[] } {
  const sp = ROCK_SPEC[kind];
  const r = rng(0x9e3779b9 ^ (ROCK_KINDS.indexOf(kind) * 7919 + variant * 104729));
  const seed = Math.floor(r() * 1e6);
  const { pos, idx } = cubeSphere(n);
  // per-variant proportions (±18 %)
  const sx = sp.size[0] * (0.82 + 0.36 * r()), sy = sp.size[1] * (0.82 + 0.36 * r()), sz = sp.size[2] * (0.82 + 0.36 * r());
  // fracture planes (cliffs keep their FRONT face (+Z) and back broad: planes are biased to the sides/top)
  const planes: Array<{ n: [number, number, number]; c: number }> = [];
  for (let k = 0; k < sp.planes; k++) {
    let nx = r() * 2 - 1, ny = r() * 1.6 - 0.5, nz = r() * 2 - 1;
    if (kind === 'cliff') nz *= 0.45;
    const l = Math.hypot(nx, ny, nz) || 1; nx /= l; ny /= l; nz /= l;
    const ext = Math.abs(nx) * sx / 2 + Math.abs(ny) * sy / 2 + Math.abs(nz) * sz / 2;
    planes.push({ n: [nx, ny, nz], c: ext * (sp.planeDepth[0] + (sp.planeDepth[1] - sp.planeDepth[0]) * r()) });
  }
  const bandF = 1 / (0.6 + 0.8 * r()), bandPhase = r() * 10;
  const out = new Float32Array(pos.length);
  for (let i = 0; i < pos.length; i += 3) {
    let x = pos[i], y = pos[i + 1], z = pos[i + 2];
    const l = Math.hypot(x, y, z) || 1; x /= l; y /= l; z /= l;
    const sr = Math.pow(Math.pow(Math.abs(x), sp.power) + Math.pow(Math.abs(y), sp.power) + Math.pow(Math.abs(z), sp.power), -1 / sp.power);
    x *= sr * sx / 2; y *= sr * sy / 2; z *= sr * sz / 2;
    for (const p of planes) { const t = x * p.n[0] + y * p.n[1] + z * p.n[2] - p.c; if (t > 0) { x -= p.n[0] * t; y -= p.n[1] * t; z -= p.n[2] * t; } }
    // displacement along the radial direction (a cheap surface normal for a star-shaped body)
    const dl = Math.hypot(x / sx, y / sy, z / sz) || 1;
    const dx = x / sx / dl, dy = y / sy / dl, dz = z / sz / dl;
    const mag = Math.min(sx, sy, sz);
    let disp = fbm(x * sp.freq / mag * 2, y * sp.freq / mag * 2, z * sp.freq / mag * 2, seed) * sp.noise * mag;
    if (sp.strata > 0) { const tri = Math.abs(((y * bandF + bandPhase) % 1 + 1) % 1 - 0.5) * 2; disp += (tri - 0.5) * sp.strata * mag * (1 - Math.abs(dy)); }
    x += dx * disp; y += dy * disp * 0.35; z += dz * disp;
    out[i] = x; out[i + 1] = y; out[i + 2] = z;
  }
  // flatten the base, origin at the bottom centre
  let minY = Infinity, minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < out.length; i += 3) { minY = Math.min(minY, out[i + 1]); minX = Math.min(minX, out[i]); maxX = Math.max(maxX, out[i]); minZ = Math.min(minZ, out[i + 2]); maxZ = Math.max(maxZ, out[i + 2]); }
  const floor = minY + (kind === 'cliff' || kind === 'outcrop' ? 0.12 : 0.08) * sy;
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  for (let i = 0; i < out.length; i += 3) { out[i] -= cx; out[i + 1] = Math.max(out[i + 1], floor) - floor; out[i + 2] -= cz; }
  return { pos: out, idx };
}

const heights = new Map<string, number>();
/** The real height (m, at scale 1) of a (kind, variant)'s render mesh — the burial reference. */
export function rockHeight(kind: RockKind, variant: number): number {
  const nV = ROCK_SPEC[kind].variants; const v = ((variant % nV) + nV) % nV; const k = `${kind}:${v}`;
  let h = heights.get(k);
  if (h === undefined) { const { pos } = rockShape(kind, v, ROCK_SPEC[kind].n); h = 0; for (let i = 1; i < pos.length; i += 3) h = Math.max(h, pos[i]); heights.set(k, h); }
  return h;
}
